// Updated SchedulePage.js - Much Better Mobile Card Layout
import React, { useState, useEffect } from 'react';
import {
  Calendar as CalendarIcon,
  MapPin,
  Download,
  List,
  Calendar as CalendarViewIcon,
  Trophy,
  Target,
  Clock,
  ChevronRight,
} from 'lucide-react';
import { Calendar as BigCalendar, momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import sanityClient from '../lib/sanityClient';
import { Link } from 'react-router-dom';

// Setup the localizer for BigCalendar
const localizer = momentLocalizer(moment);

// Custom toolbar component
const CustomToolbar = (toolbar) => {
  const goToBack = () => {
    toolbar.onNavigate('PREV');
  };

  const goToNext = () => {
    toolbar.onNavigate('NEXT');
  };

  const goToCurrent = () => {
    toolbar.onNavigate('TODAY');
  };

  const label = () => {
    const date = moment(toolbar.date);
    return <span className="text-lg font-bold text-white">{date.format('MMMM YYYY')}</span>;
  };

  return (
    <div className="flex flex-col space-y-2 md:space-y-0 md:flex-row md:justify-between md:items-center mb-4">
      <div className="flex space-x-2">
        <button
          type="button"
          onClick={goToBack}
          className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600"
        >
          &lt;
        </button>
        <button
          type="button"
          onClick={goToCurrent}
          className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600"
        >
          Today
        </button>
        <button
          type="button"
          onClick={goToNext}
          className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600"
        >
          &gt;
        </button>
      </div>

      <div className="flex-grow text-center md:text-left ml-0 md:ml-4">{label()}</div>

      <div></div>
    </div>
  );
};

function SchedulePage() {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter states
  const [selectedEventType, setSelectedEventType] = useState('all');
  const [showUpcomingOnly, setShowUpcomingOnly] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [calendarView, setCalendarView] = useState('month');
  const [calendarDate, setCalendarDate] = useState(new Date());

  // Group events by month
  const [eventsByMonth, setEventsByMonth] = useState({});

  useEffect(() => {
    Promise.all([
      // Fetch events with game results
      sanityClient.fetch(
        `*[_type == "event"] | order(startDate asc) {
          _id,
          title,
          startDate,
          endDate,
          location,
          description,
          eventType,
          opponent,
          isHomeGame,
          allDay,
          isComplete,
          ourScore,
          opponentScore,
          ourShots,
          opponentShots
        }`
      ),
      // Fetch players with birthdates
      sanityClient.fetch(
        `*[_type == "player" && defined(birthdate)] {
          _id,
          name,
          birthdate,
          number,
          "imageUrl": image.asset->url
        }`
      ),
    ])
      .then(([eventsData, playersData]) => {
        // Generate birthday events for current and next year
        const currentYear = new Date().getFullYear();
        const birthdayEvents = [];

        playersData.forEach((player) => {
          if (player.birthdate) {
            // Parse date correctly to avoid timezone issues
            const [year, month, day] = player.birthdate.split('-').map(Number);
            const today = new Date();

            // Create birthday for current year using local timezone
            const birthdateThisYear = new Date(currentYear, month - 1, day);

            // Determine which birthday to show - current year or next year
            let birthdayToShow;
            const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

            if (birthdateThisYear > todayDateOnly) {
              // Birthday hasn't happened yet this year, show this year's
              birthdayToShow = birthdateThisYear;
            } else {
              // Birthday already passed this year, show next year's
              birthdayToShow = new Date(currentYear + 1, month - 1, day);
            }

            birthdayEvents.push({
              _id: `birthday-${player._id}-${birthdayToShow.getFullYear()}`,
              title: `🎂 ${player.name}'s Birthday`,
              startDate: birthdayToShow.toISOString(),
              start: birthdayToShow,
              end: birthdayToShow,
              allDay: true,
              eventType: 'birthday',
              player: player,
              color: '#f59e0b', // amber color for birthdays
              textColor: '#000000',
            });
          }
        });

        const formattedEvents = eventsData.map((event) => {
          const isGame = event.eventType === 'game';
          const isCompleted = isGame && event.isComplete;

          let gameResult = null;
          let resultColor = '';

          if (isCompleted && event.ourScore !== undefined && event.opponentScore !== undefined) {
            if (event.ourScore > event.opponentScore) {
              gameResult = 'W';
              resultColor = 'text-green-400';
            } else if (event.ourScore < event.opponentScore) {
              gameResult = 'L';
              resultColor = 'text-orange-400';
            } else {
              gameResult = 'T';
              resultColor = 'text-yellow-400';
            }
          }

          return {
            ...event,
            date: event.startDate,
            formattedDate: new Date(event.startDate).toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
            }),
            status: new Date(event.startDate) > new Date() ? 'upcoming' : 'past',
            gameResult,
            resultColor,
            isCompleted,
            scoreDisplay:
              isCompleted && event.ourScore !== undefined && event.opponentScore !== undefined
                ? `${event.ourScore}-${event.opponentScore}`
                : null,
          };
        });

        // Combine regular events with birthday events
        const allEvents = [...formattedEvents, ...birthdayEvents];

        setEvents(allEvents);
        setFilteredEvents(allEvents);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching events:', err);
        setError('Failed to load schedule. Please try again later.');
        setIsLoading(false);
      });
  }, []);

  // Apply filters whenever filter states change
  useEffect(() => {
    let filtered = [...events];

    if (selectedEventType !== 'all') {
      filtered = filtered.filter((event) => event.eventType === selectedEventType);
    }

    if (showUpcomingOnly) {
      filtered = filtered.filter((event) => event.status === 'upcoming');
    }

    // Sort events chronologically (earliest to latest)
    filtered.sort((a, b) => {
      const dateA = new Date(a.startDate);
      const dateB = new Date(b.startDate);
      return dateA - dateB;
    });

    setFilteredEvents(filtered);

    // Group by month for list view (preserve the sorted order within each month)
    const grouped = {};
    filtered.forEach((event) => {
      const date = new Date(event.startDate);
      const monthYear = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

      if (!grouped[monthYear]) {
        grouped[monthYear] = [];
      }

      grouped[monthYear].push(event);
    });

    setEventsByMonth(grouped);
  }, [events, selectedEventType, showUpcomingOnly]);

  // Extract unique event types for the filter
  const eventTypes = Array.from(new Set(events.map((event) => event.eventType)));

  // Format events for calendar
  const calendarEvents = filteredEvents.map((event) => {
    let title =
      event.eventType === 'game'
        ? `${event.isHomeGame ? 'vs' : '@'} ${event.opponent}`
        : event.title;

    if (event.isCompleted && event.scoreDisplay) {
      title += ` (${event.gameResult} ${event.scoreDisplay})`;
    }

    return {
      id: event._id,
      title,
      start: new Date(event.startDate),
      end: event.endDate ? new Date(event.endDate) : new Date(event.startDate),
      allDay: event.allDay || false,
      resource: event,
    };
  });

  // Get event background color based on event type and result
  const eventStyleGetter = (event) => {
    let backgroundColor = '#3174ad';

    if (event.resource) {
      const eventResource = event.resource;
      const eventType = eventResource.eventType;

      if (eventType === 'game' && eventResource.isCompleted) {
        if (eventResource.gameResult === 'W') {
          backgroundColor = '#22c55e';
        } else if (eventResource.gameResult === 'L') {
          backgroundColor = '#f97316';
        } else if (eventResource.gameResult === 'T') {
          backgroundColor = '#eab308';
        }
      } else {
        switch (eventType) {
          case 'game':
            backgroundColor = eventResource.isHomeGame ? '#4CAF50' : '#FF9800';
            break;
          case 'practice':
            backgroundColor = '#2196F3';
            break;
          case 'tournament':
            backgroundColor = '#9C27B0';
            break;
          case 'meeting':
            backgroundColor = '#607D8B';
            break;
          case 'birthday':
            backgroundColor = '#f59e0b'; // amber for birthdays
            break;
          default:
            backgroundColor = '#3174ad';
        }
      }
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 0.8,
        color: 'white',
        border: '0px',
        display: 'block',
      },
    };
  };

  // Function to download schedule as iCal file
  const downloadSchedule = () => {
    let iCalContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Bombardiers Hockey//Schedule//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'X-WR-CALNAME:Bombardiers Hockey Schedule',
      'X-WR-TIMEZONE:America/New_York',
    ];

    filteredEvents.forEach((event) => {
      const startDate = moment(event.startDate).format('YYYYMMDDTHHmmss');
      const endDate = event.endDate
        ? moment(event.endDate).format('YYYYMMDDTHHmmss')
        : moment(event.startDate).add(2, 'hours').format('YYYYMMDDTHHmmss');

      let summary = event.title || `${event.eventType} - ${event.opponent || ''}`;

      if (event.isCompleted && event.scoreDisplay) {
        summary += ` (${event.gameResult} ${event.scoreDisplay})`;
      }

      iCalContent.push('BEGIN:VEVENT');
      iCalContent.push(`UID:${event._id}@bombardiershockey.com`);
      iCalContent.push(`DTSTAMP:${moment().format('YYYYMMDDTHHmmss')}`);
      iCalContent.push(`DTSTART:${startDate}`);
      iCalContent.push(`DTEND:${endDate}`);
      iCalContent.push(`SUMMARY:${summary}`);
      if (event.location) iCalContent.push(`LOCATION:${event.location}`);
      if (event.description) iCalContent.push(`DESCRIPTION:${event.description}`);
      iCalContent.push('END:VEVENT');
    });

    iCalContent.push('END:VCALENDAR');

    const blob = new Blob([iCalContent.join('\n')], { type: 'text/calendar' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bombardiers-hockey-schedule.ics';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  // Determine if event should show details button
  const shouldShowDetails = (event) => {
    return event.eventType === 'game' && (event.isCompleted || event.hasArticle);
  };

  // Mobile Event Card Component
  const MobileEventCard = ({ event }) => {
    const isGame = event.eventType === 'game';
    const isBirthday = event.eventType === 'birthday';

    return (
      <div className="bg-gray-900 rounded-xl p-4 border border-gray-800 hover:border-yellow-600 transition-all">
        {/* Date Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center text-yellow-500 font-medium">
            <CalendarIcon className="h-4 w-4 mr-2" />
            {new Date(event.startDate).toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
            })}
          </div>
          {/* Hide time for birthday events */}
          {!isBirthday && (
            <div className="flex items-center text-gray-400 text-sm">
              <Clock className="h-4 w-4 mr-1" />
              {new Date(event.startDate).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
              })}
            </div>
          )}
        </div>

        {/* Event Content */}
        {isGame ? (
          <div className="mb-4">
            {/* Team Matchup */}
            <div className="text-center mb-3">
              <div className="text-lg font-bold text-white mb-1">
                {event.isHomeGame ? (
                  <>
                    <span className="text-yellow-500">BOMBARDIERS</span>
                    <span className="text-gray-400 mx-3">vs</span>
                    <span className="text-gray-300">{event.opponent}</span>
                  </>
                ) : (
                  <>
                    <span className="text-gray-300">{event.opponent}</span>
                    <span className="text-gray-400 mx-3">vs</span>
                    <span className="text-yellow-500">BOMBARDIERS</span>
                  </>
                )}
              </div>

              {/* Game Result */}
              {event.isCompleted && event.scoreDisplay && (
                <div className="flex items-center justify-center">
                  <div
                    className={`flex items-center space-x-2 px-3 py-1 rounded-full ${
                      event.gameResult === 'W'
                        ? 'bg-green-900'
                        : event.gameResult === 'L'
                          ? 'bg-transparent'
                          : 'bg-yellow-900'
                    }`}
                  >
                    {event.gameResult === 'W' && <Trophy className="h-4 w-4 text-green-400" />}
                    {event.gameResult === 'L' && <Target className="h-4 w-4 text-orange-400" />}
                    <span className={`font-bold ${event.resultColor}`}>
                      {event.gameResult} {event.scoreDisplay}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="mb-4">
            <h3 className="text-lg font-medium text-white text-center">
              {event.title ||
                `${event.eventType.charAt(0).toUpperCase() + event.eventType.slice(1)}`}
            </h3>
          </div>
        )}

        {/* Location - Hide for birthday events */}
        {!isBirthday && (
          <div className="flex items-center justify-center text-gray-400 text-sm mb-4">
            <MapPin className="h-4 w-4 mr-2" />
            {event.location || 'Location TBD'}
          </div>
        )}

        {/* Bottom Section */}
        <div className="flex flex-col items-center space-y-3">
          {/* Status Badge - Only show if upcoming */}
          {event.status === 'upcoming' && (
            <div className="px-3 py-1 rounded-full text-xs font-medium bg-blue-900 text-blue-200">
              UPCOMING
            </div>
          )}

          {/* Details Button - Centered */}
          {shouldShowDetails(event) && (
            <Link
              to={`/games/${event._id}`}
              className="flex items-center bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-full text-sm font-medium transition-colors"
            >
              View Details
              <ChevronRight className="h-4 w-4 ml-1" />
            </Link>
          )}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="bg-black min-h-screen flex justify-center items-center">
        <div className="text-white text-xl">Loading schedule...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-black min-h-screen flex justify-center items-center">
        <div className="bg-gray-900 p-6 rounded-lg text-white text-xl">{error}</div>
      </div>
    );
  }

  return (
    <div className="bg-black min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:justify-between md:items-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white text-center md:text-left">
            Team Schedule
          </h1>

          {/* Control Buttons */}
          <div className="flex flex-col space-y-2 md:space-y-0 md:flex-row md:space-x-2">
            <button
              className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-md flex items-center justify-center font-medium transition-colors"
              onClick={downloadSchedule}
            >
              <Download className="h-4 w-4 mr-2" />
              Download Schedule
            </button>

            {/* View Toggle Buttons */}
            <div className="flex space-x-2">
              <button
                className={`flex-1 md:flex-none px-4 py-2 rounded-md flex items-center justify-center font-medium transition-colors ${
                  viewMode === 'list'
                    ? 'bg-gray-600 text-white'
                    : 'bg-gray-800 text-gray-300 hover:text-white'
                }`}
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4 mr-2" />
                List
              </button>
              <button
                className={`flex-1 md:flex-none px-4 py-2 rounded-md flex items-center justify-center font-medium transition-colors ${
                  viewMode === 'calendar'
                    ? 'bg-gray-600 text-white'
                    : 'bg-gray-800 text-gray-300 hover:text-white'
                }`}
                onClick={() => setViewMode('calendar')}
              >
                <CalendarViewIcon className="h-4 w-4 mr-2" />
                Calendar
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-gray-900 rounded-lg p-4 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-400 text-sm mb-1">Event Type</label>
              <select
                className="w-full bg-gray-800 text-white py-2 px-3 rounded-md"
                value={selectedEventType}
                onChange={(e) => setSelectedEventType(e.target.value)}
              >
                <option value="all">All Events</option>
                {eventTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="w-4 h-4 mr-2 accent-yellow-600"
                  checked={showUpcomingOnly}
                  onChange={() => setShowUpcomingOnly(!showUpcomingOnly)}
                />
                <span className="text-white">Show Upcoming Events Only</span>
              </label>
            </div>
          </div>
        </div>

        {/* Calendar View */}
        {viewMode === 'calendar' && (
          <div className="bg-gray-900 rounded-lg p-4 mb-8">
            {/* Month Header */}
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-yellow-500">
                {moment(calendarDate).format('MMMM YYYY')}
              </h2>
            </div>

            <div className="mb-4 flex flex-wrap">
              <button
                onClick={() => setCalendarView('month')}
                className={`px-4 py-2 rounded-l ${calendarView === 'month' ? 'bg-gray-700 text-white' : 'bg-gray-800 text-gray-300'}`}
              >
                Month
              </button>
              <button
                onClick={() => setCalendarView('week')}
                className={`px-4 py-2 ${calendarView === 'week' ? 'bg-gray-700 text-white' : 'bg-gray-800 text-gray-300'}`}
              >
                Week
              </button>
              <button
                onClick={() => setCalendarView('day')}
                className={`px-4 py-2 ${calendarView === 'day' ? 'bg-gray-700 text-white' : 'bg-gray-800 text-gray-300'}`}
              >
                Day
              </button>
              <button
                onClick={() => setCalendarView('agenda')}
                className={`px-4 py-2 rounded-r ${calendarView === 'agenda' ? 'bg-gray-700 text-white' : 'bg-gray-800 text-gray-300'}`}
              >
                Agenda
              </button>
            </div>

            <div style={{ height: '700px' }} className="bg-white rounded-lg p-4">
              <BigCalendar
                localizer={localizer}
                events={calendarEvents}
                startAccessor="start"
                endAccessor="end"
                defaultView={Views.MONTH}
                view={calendarView}
                onView={setCalendarView}
                date={calendarDate}
                onNavigate={setCalendarDate}
                components={{
                  toolbar: CustomToolbar,
                }}
                eventPropGetter={eventStyleGetter}
                onSelectEvent={(event) => {
                  // console.log('Event clicked:', event);
                }}
              />
            </div>
          </div>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <>
            {Object.keys(eventsByMonth).length > 0 ? (
              Object.entries(eventsByMonth).map(([month, monthEvents]) => (
                <div key={month} className="mb-8">
                  <h2 className="text-xl font-bold text-yellow-500 mb-6">{month}</h2>

                  {/* Mobile Card Layout */}
                  <div className="space-y-4">
                    {monthEvents.map((event) => (
                      <MobileEventCard key={event._id} event={event} />
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-gray-900 p-8 rounded-lg text-center">
                <p className="text-gray-400 text-lg">No events found matching your filters.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default SchedulePage;
